import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Calendar, Clock, MapPin, Users, Bell } from "lucide-react";
import { format } from "date-fns";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

interface EventCardProps {
  event: {
    id: string;
    title: string;
    description: string | null;
    event_type: string;
    scheduled_at: string;
    duration_minutes: number | null;
    location: string | null;
    thumbnail_url: string | null;
    max_participants: number | null;
    creator_id: string;
    profiles?: {
      display_name: string | null;
      username: string;
      avatar_url: string | null;
      is_verified: boolean | null;
    };
    participant_count?: number;
    user_rsvp?: string | null;
  };
}

export function EventCard({ event }: EventCardProps) {
  const { user } = useAuth();
  const [rsvpStatus, setRsvpStatus] = useState(event.user_rsvp || null);
  const [participantCount, setParticipantCount] = useState(event.participant_count || 0);
  const [isLoading, setIsLoading] = useState(false);
  const [notificationPrefs, setNotificationPrefs] = useState({
    notify_1_hour_before: true,
    notify_1_day_before: true,
    notify_1_week_before: false,
  });

  useEffect(() => {
    const loadNotificationPrefs = async () => {
      if (!rsvpStatus || !user) return;

      const { data, error } = await supabase
        .from("event_notification_preferences")
        .select("*")
        .eq("user_id", user.id)
        .eq("event_id", event.id)
        .single();

      if (!error && data) {
        setNotificationPrefs({
          notify_1_hour_before: data.notify_1_hour_before,
          notify_1_day_before: data.notify_1_day_before,
          notify_1_week_before: data.notify_1_week_before,
        });
      }
    };

    loadNotificationPrefs();
  }, [rsvpStatus, user, event.id]);

  const handleRSVP = async (status: 'interested' | 'going' | 'remove') => {
    if (!user) {
      toast.error("Please sign in to RSVP");
      return;
    }

    setIsLoading(true);
    try {
      if (status === 'remove') {
        // Remove RSVP and notification preferences
        await supabase
          .from('event_participants')
          .delete()
          .eq('event_id', event.id)
          .eq('user_id', user.id);

        await supabase
          .from('event_notification_preferences')
          .delete()
          .eq('event_id', event.id)
          .eq('user_id', user.id);

        setRsvpStatus(null);
        setParticipantCount(prev => Math.max(0, prev - 1));
        toast.success("RSVP removed");
      } else {
        if (rsvpStatus) {
          // Update existing RSVP
          const { error } = await supabase
            .from('event_participants')
            .update({ status })
            .eq('event_id', event.id)
            .eq('user_id', user.id);

          if (error) throw error;
        } else {
          // Create new RSVP
          const { error } = await supabase
            .from('event_participants')
            .insert({ event_id: event.id, user_id: user.id, status });

          if (error) throw error;
          setParticipantCount(prev => prev + 1);
        }

        // Create/update notification preferences
        await supabase
          .from('event_notification_preferences')
          .upsert({
            user_id: user.id,
            event_id: event.id,
            ...notificationPrefs,
          });

        setRsvpStatus(status);
        toast.success(status === 'going' ? "You're going!" : "Marked as interested");
      }
    } catch (error) {
      console.error('Error updating RSVP:', error);
      toast.error("Failed to update RSVP");
    } finally {
      setIsLoading(false);
    }
  };

  const handleNotificationToggle = async (field: keyof typeof notificationPrefs) => {
    if (!user) return;

    const newValue = !notificationPrefs[field];
    setNotificationPrefs(prev => ({ ...prev, [field]: newValue }));

    try {
      const { error } = await supabase
        .from('event_notification_preferences')
        .upsert({
          user_id: user.id,
          event_id: event.id,
          [field]: newValue,
        });

      if (error) throw error;
      toast.success("Notification preferences updated");
    } catch (error) {
      console.error('Error updating notification preferences:', error);
      toast.error("Failed to update preferences");
      setNotificationPrefs(prev => ({ ...prev, [field]: !newValue }));
    }
  };

  const getEventTypeColor = (type: string) => {
    switch (type) {
      case 'stream': return 'bg-primary/10 text-primary';
      case 'meetup': return 'bg-secondary/10 text-secondary';
      case 'announcement': return 'bg-accent/10 text-accent';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <Card className="overflow-hidden hover:shadow-lg transition-shadow">
      <div className="relative h-48 bg-gradient-to-br from-primary/20 to-secondary/20">
        {event.thumbnail_url ? (
          <img
            src={event.thumbnail_url}
            alt={event.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Calendar className="w-16 h-16 text-muted-foreground/30" />
          </div>
        )}
        <Badge className={`absolute top-2 left-2 ${getEventTypeColor(event.event_type)}`}>
          {event.event_type}
        </Badge>
      </div>

      <div className="p-4 space-y-3">
        <div>
          <h3 className="font-semibold text-lg line-clamp-1">{event.title}</h3>
          {event.description && (
            <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
              {event.description}
            </p>
          )}
        </div>

        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Calendar className="w-4 h-4" />
            <span>{format(new Date(event.scheduled_at), 'MMM dd, yyyy')}</span>
          </div>
          
          <div className="flex items-center gap-2 text-muted-foreground">
            <Clock className="w-4 h-4" />
            <span>
              {format(new Date(event.scheduled_at), 'h:mm a')}
              {event.duration_minutes && ` • ${event.duration_minutes} min`}
            </span>
          </div>

          {event.location && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <MapPin className="w-4 h-4" />
              <span className="line-clamp-1">{event.location}</span>
            </div>
          )}

          <div className="flex items-center gap-2 text-muted-foreground">
            <Users className="w-4 h-4" />
            <span>
              {participantCount} interested
              {event.max_participants && ` • ${event.max_participants} max`}
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between pt-2">
          <div className="flex items-center gap-2">
            {event.profiles?.avatar_url ? (
              <img
                src={event.profiles.avatar_url}
                alt={event.profiles.display_name || event.profiles.username}
                className="w-8 h-8 rounded-full object-cover"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                <span className="text-xs font-medium">
                  {(event.profiles?.display_name || event.profiles?.username || '?')[0].toUpperCase()}
                </span>
              </div>
            )}
            <span className="text-sm font-medium">
              {event.profiles?.display_name || event.profiles?.username}
            </span>
          </div>

        <div className="flex flex-col gap-3">
          <div className="flex gap-2">
            <Button
              size="sm"
              variant={rsvpStatus === 'interested' ? 'default' : 'outline'}
              onClick={() => handleRSVP('interested')}
              disabled={isLoading}
            >
              Interested
            </Button>
            <Button
              size="sm"
              variant={rsvpStatus === 'going' ? 'default' : 'outline'}
              onClick={() => handleRSVP('going')}
              disabled={isLoading}
            >
              Going
            </Button>
            {rsvpStatus && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleRSVP('remove')}
                disabled={isLoading}
              >
                Remove
              </Button>
            )}
          </div>

          {/* Notification Preferences */}
          {(rsvpStatus === 'interested' || rsvpStatus === 'going') && (
            <div className="p-3 bg-muted/50 rounded-lg space-y-2.5">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Bell className="w-4 h-4" />
                <span>Remind me:</span>
              </div>
              <div className="space-y-2 pl-6">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id={`${event.id}-1hour`}
                    checked={notificationPrefs.notify_1_hour_before}
                    onCheckedChange={() => handleNotificationToggle("notify_1_hour_before")}
                  />
                  <Label 
                    htmlFor={`${event.id}-1hour`}
                    className="text-sm font-normal cursor-pointer"
                  >
                    1 hour before
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id={`${event.id}-1day`}
                    checked={notificationPrefs.notify_1_day_before}
                    onCheckedChange={() => handleNotificationToggle("notify_1_day_before")}
                  />
                  <Label 
                    htmlFor={`${event.id}-1day`}
                    className="text-sm font-normal cursor-pointer"
                  >
                    1 day before
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id={`${event.id}-1week`}
                    checked={notificationPrefs.notify_1_week_before}
                    onCheckedChange={() => handleNotificationToggle("notify_1_week_before")}
                  />
                  <Label 
                    htmlFor={`${event.id}-1week`}
                    className="text-sm font-normal cursor-pointer"
                  >
                    1 week before
                  </Label>
                </div>
              </div>
            </div>
          )}
        </div>
        </div>
      </div>
    </Card>
  );
}
